import { useHandler } from "@redotech/react-util/hook";
import { memo, useEffect, useRef, useState } from "react";
import * as styles from "./panel-resizer.module.css";

export interface PanelResizerProps {
  onResize: (delta: number) => void;
  onResizeEnd?: () => void;
}

export const PanelResizer = memo(function PanelResizer({
  onResize,
  onResizeEnd,
}: PanelResizerProps) {
  const isDraggingRef = useRef(false);
  const lastXRef = useRef(0);
  const [isResizing, setIsResizing] = useState(false);

  const startResize = useHandler((e: React.MouseEvent) => {
    e.preventDefault();
    isDraggingRef.current = true;
    lastXRef.current = e.clientX;
    setIsResizing(true);
    document.body.style.cursor = "col-resize";
  });

  const stopResize = useHandler(() => {
    if (isDraggingRef.current) {
      isDraggingRef.current = false;
      setIsResizing(false);
      document.body.style.cursor = "";
      onResizeEnd?.();
    }
  });

  const resize = useHandler((e: MouseEvent) => {
    if (!isDraggingRef.current) return;
    const delta = e.clientX - lastXRef.current;
    lastXRef.current = e.clientX;
    onResize(delta);
  });

  useEffect(() => {
    addEventListener("mousemove", resize);
    addEventListener("mouseup", stopResize);
    return () => {
      removeEventListener("mousemove", resize);
      removeEventListener("mouseup", stopResize);
    };
  }, [resize, stopResize]);

  return (
    <div
      className={styles.resizer}
      data-resizing={isResizing || undefined}
      onMouseDown={startResize}
    />
  );
});
